import { auth, type SchemaResponse } from "@/modules/api/axios"
import type { PagingSchema } from "@/modules/types/common/schema"
export interface AIConversationMessage {
  content: string
  role: "user" | "assistant"
}

export interface AIRequest {
  qs: string
  conversation_history?: AIConversationMessage[]
}

export interface AIResponse {
  text: string
}

export interface AIChatRequest {
  content: string
  role: "KOC" | "AI"
}

export interface AIChatMessage {
  id: string
  content: string
  role: "KOC" | "AI"
  createdAt: string
  updatedAt: string
}

export interface QuestionAnswerRequest {
  question: string
  createdFrom: "AI"
}

const AI_BASE_URL = "/ai-chat/ask"

export const sendMessageToAI = (request: AIRequest) => {
  return auth.post<AIResponse>(AI_BASE_URL, request, {
    headers: {
      "Cache-Control": "no-cache",
      "Content-Type": "application/json"
    },
    timeout: 30000 // 30 seconds timeout
  })
}

export const sendToBD = (request: { question: string }) => {
  return auth.post<AIResponse>("ai-chat/noti-slack/question", request, {
    headers: {
      "Cache-Control": "no-cache",
      "Content-Type": "application/json"
    }
  })
}

export const getAIChatMessages = async (page = 1, take = 20) => {
  return await auth.get<SchemaResponse<PagingSchema<AIChatMessage>>>("/ai-chat/list", {
    params: {
      page,
      take,
      sortBy: "createdAt",
      order: "DESC"
    }
  })
}

export const saveAIChatMessage = async (request: AIChatRequest): Promise<void> => {
  await auth.post<void>("/ai-chat", request, {
    headers: {
      "Cache-Control": "no-cache",
      "Content-Type": "application/json"
    }
  })
}

export const saveAIChatMessages = async (requests: AIChatRequest[]): Promise<void> => {
  // Gửi từng tin nhắn một cách tuần tự để đảm bảo thứ tự
  for (const request of requests) {
    await saveAIChatMessage(request)
  }
}

export const postQuestionAnswer = async (request: QuestionAnswerRequest): Promise<void> => {
  await auth.post<void>("/questions-and-answers", request, {
    headers: {
      "Cache-Control": "no-cache",
      "Content-Type": "application/json"
    }
  })
}

// Hook cho React Query
export const getAIChatQuery = () => {
  const sendMessage = async (
    question: string,
    conversationHistory?: AIConversationMessage[],
    _userId?: number
  ) => {
    const request: AIRequest = {
      qs: question,
      ...(conversationHistory?.length && { conversation_history: conversationHistory })
    }

    return await sendMessageToAI(request)
  }

  const saveChatMessage = async (content: string, role: "KOC" | "AI") => {
    const request: AIChatRequest = {
      content,
      role
    }

    return await saveAIChatMessage(request)
  }

  const saveChatMessages = async (messages: AIChatRequest[]) => {
    return await saveAIChatMessages(messages)
  }

  const sendQuestionAnswer = async (question: string) => {
    const request: QuestionAnswerRequest = {
      question,
      createdFrom: "AI"
    }
    return await postQuestionAnswer(request)
  }

  return { sendMessage, saveChatMessage, saveChatMessages, sendQuestionAnswer }
}
